import React, { useCallback, useMemo, useState } from 'react'
import { DrawerContext } from '../lib/DrawerContext'
import { useBool } from '@/shared/lib/useBool'

const INITIAL_DRAWER = {
  content: null,
  settings: {},
  onClose: null,
  id: null,
}

/**
 * @typedef {Object} DrawerSettings
 * @property {number} [width] - Ширина панели в пикселях
 */

/**
 * @typedef {Object} DrawerConfig
 * @property {React.ReactNode} content - Содержимое панели
 * @property {DrawerSettings} [settings] - Настройки панели
 * @property {Function} [onClose] - Обработчик закрытия
 * @property {string} [id] - Идентификатор содержимого
 */

/**
 * Провайдер состояния боковой Drawer-панели.
 */
export const DrawerProvider = ({ children }) => {
  const { value: isOpen, onFalse: setClosed, onTrue: setOpen } = useBool(false)
  const [drawer, setDrawer] = useState(INITIAL_DRAWER)

  const openDrawer = useCallback(
    config => {
      setDrawer({ ...INITIAL_DRAWER, ...config })
      setOpen()
    },
    [setOpen],
  )

  const updateDrawer = useCallback(config => {
    setDrawer(current => ({
      ...current,
      ...config,
      settings: { ...current.settings, ...config.settings },
    }))
  }, [])

  const closeDrawer = useCallback(() => {
    const onClose = drawer.onClose

    setClosed()
    setDrawer(INITIAL_DRAWER)
    onClose?.()
  }, [drawer.onClose, setClosed])

  const toggleDrawer = useCallback(
    config => {
      const isSameDrawer = config.id ? drawer.id === config.id : drawer.content === config.content

      if (isOpen && isSameDrawer) {
        closeDrawer()
        return
      }

      openDrawer(config)
    },
    [closeDrawer, drawer.content, drawer.id, isOpen, openDrawer],
  )

  const value = useMemo(
    () => ({
      isOpen,
      content: drawer.content,
      settings: drawer.settings,
      id: drawer.id,
      openDrawer,
      updateDrawer,
      closeDrawer,
      toggleDrawer,
    }),
    [
      closeDrawer,
      drawer.content,
      drawer.id,
      drawer.settings,
      isOpen,
      openDrawer,
      toggleDrawer,
      updateDrawer,
    ],
  )

  return <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>
}
